import { Dispatch, FC, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AttachmentResponse } from '@/api/data-contracts';
import { ReactComponent as CloseIcon } from '@/assets/icons/ChevronRightRounded.svg';
import { Comments } from '@/features/comments';
import {
  CloseButton,
  Container,
  Content,
  Header,
  Title,
} from '@/features/media/ui/FileManager/MenuContent.styles';
import FileVersions from '@/features/media/ui/FileVersions';
import { KeyVisualsTasks } from '@/features/orders';
import UnreadCountBadge from '@/shared/ui/UnreadCountBadge';

interface Props {
  attachment: AttachmentResponse;
  contentType: 'comments' | 'tasks' | 'versions' | 'info' | undefined;
  setContentType: Dispatch<SetStateAction<'comments' | 'tasks' | 'versions' | 'info' | undefined>>;
  activeFileIndex: number;
  files: File[];
  setFiles: Dispatch<SetStateAction<File[]>>;
  setActiveFile: Dispatch<SetStateAction<File | undefined>>;
  mediaPlanId?: string;
  orderId?: string;
  bookingId?: string;
  advertisingSpaceId?: string;
  taskId?: string;
  commentsThreadId?: string;
  disableVersionUpload?: boolean;
  category?: 'keyVisual' | 'artWork';
}

const MenuContent: FC<Props> = ({
  attachment,
  contentType,
  setContentType,
  activeFileIndex,
  files,
  setFiles,
  setActiveFile,
  orderId,
  bookingId,
  mediaPlanId,
  advertisingSpaceId,
  taskId,
  commentsThreadId,
  disableVersionUpload,
  category,
}) => {
  const { t } = useTranslation();
  const [unreadCommentsCount, setUnreadCommentsCount] = useState(0);

  if (!contentType) {
    return null;
  }

  return (
    <Container>
      <Header>
        <Title>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            {t(contentType)}
            {contentType === 'comments' && unreadCommentsCount > 0 && (
              <UnreadCountBadge component="span" label={unreadCommentsCount} />
            )}
          </span>
        </Title>
        <CloseButton onClick={() => setContentType(undefined)}>
          <CloseIcon />
        </CloseButton>
      </Header>

      <Content>
        {contentType === 'tasks' && orderId && (
          <KeyVisualsTasks orderId={orderId} attachment={attachment} />
        )}

        {contentType === 'comments' && (
          <Comments
            key={`comments${attachment.id}`}
            orderId={orderId}
            taskId={taskId}
            mediaPlanId={mediaPlanId}
            attachmentId={attachment.id}
            commentsThreadId={commentsThreadId}
            onUnreadCountChange={setUnreadCommentsCount}
          />
        )}

        {contentType === 'versions' && (
          <FileVersions
            key={`versions${attachment.id}`}
            attachment={attachment}
            activeFileIndex={activeFileIndex}
            files={files}
            setFiles={setFiles}
            setActiveFile={setActiveFile}
            mediaPlanId={mediaPlanId}
            orderId={orderId}
            bookingId={bookingId}
            advertisingSpaceId={advertisingSpaceId}
            disableVersionUpload={disableVersionUpload}
            category={category}
          />
        )}
      </Content>
    </Container>
  );
};

export default MenuContent;
